import { useCallback, useEffect, useState } from "react";

import {
  getWorkflowStatus,
  type WorkflowStatusResponse,
} from "api/applications";

interface Snapshot {
  status: WorkflowStatusResponse | null;
  loading: boolean;
  error: string | null;
}

const EMPTY: Snapshot = { status: null, loading: false, error: null };

const cache = new Map<string, Snapshot>();
const listeners = new Map<string, Set<(snap: Snapshot) => void>>();

function emit(leadId: string, snap: Snapshot): void {
  cache.set(leadId, snap);
  listeners.get(leadId)?.forEach((cb) => cb(snap));
}

async function load(leadId: string): Promise<void> {
  const prev = cache.get(leadId) ?? EMPTY;
  emit(leadId, { status: prev.status, loading: true, error: null });
  try {
    const status = await getWorkflowStatus(leadId);
    emit(leadId, { status, loading: false, error: null });
  } catch (err) {
    emit(leadId, {
      status: prev.status,
      loading: false,
      error: err instanceof Error ? err.message : "Failed to load workflow status",
    });
  }
}

export interface UseWorkflowStatusResult extends Snapshot {
  refresh: () => Promise<void>;
}

/**
 * Subscribes to a lead's workflow status. Multiple components calling this for the
 * same leadId share one in-flight request and one cached snapshot — so the network
 * is hit once even when many components mount together. Call `refresh()` after a
 * mutation to re-fetch and notify all subscribers.
 */
export function useWorkflowStatus(
  leadId: string | null | undefined,
): UseWorkflowStatusResult {
  const [snapshot, setSnapshot] = useState<Snapshot>(() => {
    if (!leadId) return EMPTY;
    return cache.get(leadId) ?? { status: null, loading: true, error: null };
  });

  useEffect(() => {
    if (!leadId) {
      setSnapshot(EMPTY);
      return;
    }

    let set = listeners.get(leadId);
    if (!set) {
      set = new Set();
      listeners.set(leadId, set);
    }
    set.add(setSnapshot);

    const cached = cache.get(leadId);
    if (cached) {
      setSnapshot(cached);
      if (!cached.status && !cached.loading) {
        void load(leadId);
      }
    } else {
      void load(leadId);
    }

    return () => {
      const current = listeners.get(leadId);
      if (!current) return;
      current.delete(setSnapshot);
      if (current.size === 0) {
        listeners.delete(leadId);
      }
    };
  }, [leadId]);

  const refresh = useCallback(async () => {
    if (!leadId) return;
    await load(leadId);
  }, [leadId]);

  return { ...snapshot, refresh };
}

/** Invalidate the cached snapshot for a lead (e.g. after a server-side mutation). */
export function invalidateWorkflowStatus(leadId: string): void {
  cache.delete(leadId);
}
